// DialogHost — single host component for the dialog system.
//
// Subscribes to src/lib/dialog.js and renders the top dialog on the stack.
// Mount once near the root of the app (App.jsx). Multiple stacked dialogs
// render as overlays from bottom to top.

import React, { useEffect, useRef, useState } from 'react'
import { _subscribe, _resolve } from '../lib/dialog'
import HoldToConfirm from './HoldToConfirm'
import { pill, card } from '../lib/studioStyles.js'

const C = {
  backdrop: 'rgba(0,0,0,0.55)',
  bg:       '#1a1d28',
  border:   '#2a2c36',
  text:     '#e1e1ed',
  dim:      '#8f8fa2',
  primary:  '#bec2ff',
  danger:   '#f87171',
  warning:  '#fbbf24',
  ok:       '#86efac',
  fieldBg:  '#0c0e15', // shared with btnStyle's non-primary background
}

export default function DialogHost() {
  const [stack, setStack] = useState([])
  useEffect(() => _subscribe(setStack), [])
  if (stack.length === 0) return null
  return (
    <>
      {stack.map((entry, i) => (
        <DialogModal key={entry.id} entry={entry} z={2000 + i} active={i === stack.length - 1} />
      ))}
    </>
  )
}

function DialogModal({ entry, z, active }) {
  const { kind, title, body, label, confirmLabel, cancelLabel, okLabel, danger, dismissible, variant, options, holdToConfirm, initialValue, selectAll, placeholder, maxLength, validate } = entry.payload
  const cardRef = useRef(null)
  const primaryRef = useRef(null)
  const inputRef = useRef(null)

  // Prompt-only local state — a fresh DialogModal instance mounts per push
  // (entry.id is the React key), so this never leaks between prompts.
  const [value, setValue] = useState(kind === 'prompt' ? (initialValue ?? '') : '')
  const trimmed = value.trim()
  const promptError = kind === 'prompt' && validate ? validate(trimmed) : null
  const promptDisabled = kind === 'prompt' && (trimmed.length === 0 || !!promptError)

  // Autofocus the primary action/input — run ONCE when this dialog becomes
  // active, never again. This used to live in the same effect as the keydown
  // listener below, which depends on `trimmed` (changes every keystroke) —
  // that made `inputRef.current?.select()` re-run on every keystroke too,
  // so with `selectAll` the just-typed character was immediately re-selected
  // and the NEXT keystroke replaced it, i.e. typing "My" produced "y".
  useEffect(() => {
    if (!active) return
    if (kind === 'prompt') {
      inputRef.current?.focus()
      if (selectAll) inputRef.current?.select()
    } else {
      primaryRef.current?.focus()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deliberately NOT
    // re-running on selectAll/kind changes mid-life; a dialog's kind/selectAll
    // are fixed for its lifetime (fresh instance per push), only `active`
    // toggling (this dialog reaching the top of the stack) should refocus it.
  }, [active])

  useEffect(() => {
    if (!active) return
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (kind === 'confirm') {
          if (dismissible !== false) { e.preventDefault(); _resolve(false) }
        } else if (kind === 'choose' || kind === 'prompt') {
          if (dismissible !== false) { e.preventDefault(); _resolve(null) }
        } else {
          e.preventDefault(); _resolve(undefined)
        }
      } else if (e.key === 'Enter') {
        // Don't steal Enter from textareas etc.
        if (e.target?.tagName === 'TEXTAREA') return
        // A focused button already gets Enter as its native click-activation
        // — let that run instead of ALSO resolving here. Without this, Enter
        // on a focused Cancel button both clicked Cancel (resolve false/null)
        // AND fell through to this handler, which resolved true/trimmed on
        // top of it — tabbing to Cancel and pressing Enter confirmed instead.
        if (e.target?.tagName === 'BUTTON') return
        // A picker has no implicit default — the user must choose an option.
        if (kind === 'choose') return
        // A held confirm can only be completed by the hold itself — a single
        // keystroke bypassing the hold would defeat its entire purpose.
        if (kind === 'confirm' && holdToConfirm) return
        if (kind === 'prompt' && promptDisabled) return
        e.preventDefault()
        _resolve(kind === 'confirm' ? true : kind === 'prompt' ? trimmed : undefined)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [active, kind, dismissible, holdToConfirm, promptDisabled, trimmed])

  const onBackdropClick = (e) => {
    if (e.target !== e.currentTarget) return
    if (kind === 'alert') { _resolve(undefined); return }
    if (kind === 'choose' || kind === 'prompt') { if (dismissible !== false) _resolve(null); return }
    if (dismissible !== false) _resolve(false)
  }

  const accent = variant === 'error' ? C.danger
              : variant === 'warning' ? C.warning
              : danger ? C.danger
              : C.primary

  return (
    <div
      onClick={onBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby={`dlg-title-${entry.id}`}
      style={{
        position: 'fixed', inset: 0,
        background: C.backdrop,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: z,
      }}
    >
      <div
        ref={cardRef}
        style={{
          minWidth: 320, maxWidth: 480,
          background: C.bg, border: `1px solid ${C.border}`,
          ...card,
          fontFamily: 'var(--m-font)', color: C.text,
          overflow: 'hidden',
        }}
      >
        <div style={{
          padding: '12px 16px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ color: accent, fontSize: 14, lineHeight: 1 }}>
            {variant === 'error' ? '⚠' : variant === 'warning' ? '⚠' : danger ? '⚠' : 'ℹ'}
          </span>
          <span id={`dlg-title-${entry.id}`} style={{
            fontSize: 13, letterSpacing: '.04em',
            color: C.text, flex: 1,
          }}>{title}</span>
        </div>
        <div style={{
          padding: '14px 16px', fontSize: 12, lineHeight: 1.5,
          color: C.text, whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        }}>
          {body}
          {kind === 'prompt' && (
            <div style={{ marginTop: body ? 10 : 0 }}>
              {label && (
                <label style={{ display: 'block', marginBottom: 6, fontSize: 11, color: C.dim }}>
                  {label}
                </label>
              )}
              <input
                ref={inputRef}
                data-testid="dialog-prompt-input"
                type="text"
                value={value}
                placeholder={placeholder}
                maxLength={maxLength}
                onChange={(e) => setValue(e.target.value)}
                style={{
                  width: '100%', boxSizing: 'border-box',
                  background: C.fieldBg, border: `1px solid ${promptError ? C.danger : C.border}`,
                  borderRadius: 5, padding: '7px 10px',
                  color: C.text, fontFamily: 'var(--m-font)', fontSize: 12,
                }}
              />
              {promptError && (
                <div style={{ marginTop: 6, fontSize: 11, color: C.danger }}>{promptError}</div>
              )}
            </div>
          )}
        </div>
        <div style={{
          padding: '10px 12px',
          borderTop: `1px solid ${C.border}`,
          display: 'flex', justifyContent: 'flex-end', gap: 8,
          background: '#13151c',
        }}>
          {kind === 'choose' ? (
            (options ?? []).map((opt, i) => (
              <button
                key={opt.value ?? i}
                ref={i === 0 ? primaryRef : undefined}
                onClick={() => _resolve(opt.value)}
                style={btnStyle(true, !!opt.danger)}
              >{opt.label}</button>
            ))
          ) : (
            <>
              {kind === 'confirm' && (
                <button
                  // With a held confirm, autofocus lands on Cancel: focus must
                  // move INTO the dialog (or Enter re-activates whatever opened
                  // it), and the only keyboard-activatable action in a held
                  // confirm is the safe one.
                  ref={holdToConfirm ? primaryRef : undefined}
                  onClick={() => _resolve(false)}
                  style={btnStyle(false, false)}
                >{cancelLabel ?? 'Cancel'}</button>
              )}
              {kind === 'prompt' && (
                <button
                  onClick={() => _resolve(null)}
                  style={btnStyle(false, false)}
                >{cancelLabel ?? 'Cancel'}</button>
              )}
              {kind === 'prompt' && (
                <button
                  data-testid="dialog-prompt-confirm"
                  disabled={promptDisabled}
                  onClick={() => { if (!promptDisabled) _resolve(trimmed) }}
                  style={{ ...btnStyle(true, !!danger), opacity: promptDisabled ? 0.45 : 1, cursor: promptDisabled ? 'default' : 'pointer' }}
                >{confirmLabel ?? 'OK'}</button>
              )}
              {kind === 'confirm' && holdToConfirm ? (
                // The confirm action itself is gated behind a sustained hold —
                // a click (or Enter, swallowed above) resolves nothing.
                <HoldToConfirm
                  onConfirm={() => _resolve(true)}
                  confirmId={holdToConfirm.confirmId}
                  testId={holdToConfirm.testId}
                  holdMs={holdToConfirm.holdMs}
                  title={confirmLabel ?? 'OK'}
                  style={{
                    padding: '6px 16px', borderRadius: 999, minWidth: 80,
                    fontFamily: 'var(--m-font)', fontSize: 12, letterSpacing: '.02em',
                  }}
                >{confirmLabel ?? 'OK'}</HoldToConfirm>
              ) : kind === 'prompt' ? null : (
                <button
                  ref={primaryRef}
                  onClick={() => _resolve(kind === 'confirm' ? true : undefined)}
                  style={btnStyle(true, !!danger || variant === 'error')}
                >{kind === 'confirm' ? (confirmLabel ?? 'OK') : (okLabel ?? 'OK')}</button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function btnStyle(primary, danger) {
  return {
    background: primary
      ? (danger ? '#3a1a1f' : '#1a1f3a')
      : C.fieldBg,
    border: `1px solid ${
      primary
        ? (danger ? '#f8717188' : '#bec2ff66')
        : '#2a2c36'
    }`,
    color: primary
      ? (danger ? '#fca5a5' : '#bec2ff')
      : '#e1e1ed',
    padding: '6px 16px',
    ...pill,
    fontFamily: 'var(--m-font)', fontSize: 12,
    letterSpacing: '.02em',
    cursor: 'pointer',
    minWidth: 80,
  }
}
